/**
 * Cloud Initialization Service
 * 
 * This service handles the initialization logic for cloud storage context,
 * including checking existing tokens, backend verification, and setup.
 */

import cloudTokenService from './cloudTokenService'
import cloudConnectionService from './cloudConnectionService'
import cloudReconnectionService from './cloudReconnectionService'

class CloudInitializationService {
  /**
   * Initialize cloud storage context
   */
  async initializeCloudStorage(setStateFunctions, context) {
    const {
      setIsConnected,
      setConnectedProvider,
      setAccessToken,
      setRefreshToken,
      setUserInfo
    } = setStateFunctions

    try {
      // Check if we have existing cloud tokens
      const storedTokens = cloudTokenService.getStoredTokens()
      
      if (storedTokens.accessToken && storedTokens.refreshToken) {
        console.log('🔑 CloudInitialization: Found existing cloud tokens, testing connection...')
        
        // Test the connection
        const isConnected = await context.testGoogleDriveConnection()
        
        if (isConnected) {
          console.log('✅ CloudInitialization: Existing connection verified')
          setIsConnected(true)
          setConnectedProvider('google_drive')
          setAccessToken(storedTokens.accessToken)
          setRefreshToken(storedTokens.refreshToken)
          
          // Load user info if available
          if (storedTokens.userInfo) {
            try {
              setUserInfo(JSON.parse(storedTokens.userInfo))
            } catch (e) {
              console.warn('Failed to parse stored user info:', e)
            }
          }
        } else {
          console.log('❌ CloudInitialization: Existing connection failed, clearing tokens')
          context.clearCloudTokens()
        }
      } else {
        console.log('🔍 CloudInitialization: No existing cloud tokens found, attempting to get from backend...')
        
        // Try to get tokens from backend
        try {
          const authToken = localStorage.getItem('authToken')
          if (authToken) {
            await context.verifyCloudConnectionWithBackend(authToken)
          } else {
            console.warn('⚠️ CloudInitialization: No auth token available for backend verification')
          }
        } catch (error) {
          console.warn('⚠️ CloudInitialization: Failed to get tokens from backend:', error)
          // Continue with setup - tokens might be available later
        }
      }
      
      // Set up automatic reconnection (only if not onboarding)
      if (!cloudConnectionService.isInOnboarding()) {
        context.setupReconnectionTimer()
      } else {
        console.log('🔒 CloudInitialization: Onboarding detected, skipping reconnection timer setup')
      }
      
    } catch (error) {
      console.error('❌ CloudInitialization: Initialization failed:', error)
      context.clearCloudTokens()
    }
  }

  /**
   * Check if user is authenticated
   */
  isUserAuthenticated() {
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    return !!(authToken && user)
  }

  /**
   * Check if user is in onboarding
   */
  isInOnboarding() {
    return window.location.pathname.includes('/onboarding')
  }

  /**
   * Should initialize cloud storage
   */
  shouldInitialize() {
    // CRITICAL: Only initialize if user is authenticated
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 CloudInitialization: User not authenticated, skipping initialization')
      return false
    }
    
    // Skip initialization during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 CloudInitialization: Onboarding detected, skipping initialization')
      return false
    }
    
    console.log('✅ CloudInitialization: User authenticated, initializing cloud storage context')
    return true
  }
}

export default new CloudInitializationService()
